'use client';

import { CircleAlert } from 'lucide-react';
import {
  createContext,
  useContext,
  type AriaAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { useTranslation } from './i18n-context';
import styles from './field.module.css';

export const fieldStyles = styles;

export interface FieldProps {
  readonly id: string;
  readonly label: ReactNode;
  readonly hint?: ReactNode;
  readonly error?: ReactNode;
  readonly children: ReactNode;
}

/** Field içindeki girişin ipucu/hata kimlikleri (sıra 136): `aria-describedby`a eklenir. */
const FieldDescriptionContext = createContext<string | undefined>(undefined);

function useDescribedBy(own: string | undefined): string | undefined {
  const fromField = useContext(FieldDescriptionContext);
  return [own, fromField].filter(Boolean).join(' ') || undefined;
}

/** Etiket + giriş + ipucu/hata sarmalayıcısı. `children` id ile eşleşen girişi taşır. */
export function Field({ id, label, hint, error, children }: FieldProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <FieldDescriptionContext.Provider
        value={[hintId, errorId].filter(Boolean).join(' ') || undefined}
      >
        {children}
      </FieldDescriptionContext.Provider>
      {hint ? (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className={styles.error} role="alert">
          <CircleAlert className={styles.errorIcon} aria-hidden="true" />
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * `invalid` görünümle birlikte `aria-invalid="true"` da yazar: ekran okuyucu
 * kırmızı çerçeveyi görmez. Geçerli alanda nitelik HİÇ yazılmaz (`"false"`
 * da değil); çağıranın kendi `aria-invalid`i o hâlde aynen geçer.
 */
function ariaInvalid(invalid: boolean | undefined, own: AriaAttributes['aria-invalid']) {
  return invalid ? true : own;
}

type NativeInputProps = InputHTMLAttributes<HTMLInputElement> & { readonly invalid?: boolean };

export function Input({ className, invalid, ...rest }: NativeInputProps) {
  const describedBy = useDescribedBy(rest['aria-describedby']);
  return (
    <input
      {...rest}
      aria-describedby={describedBy}
      aria-invalid={ariaInvalid(invalid, rest['aria-invalid'])}
      className={[styles.input, invalid ? styles.inputError : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
    />
  );
}

/**
 * URL ALANLARININ ORTAK GİRİŞ ÖZELLİKLERİ (manuel test 2 · bulgu 5).
 *
 * BULGU: konuşmacının "kişisel web sitesi" alanına `www.emrecekci.com`
 * yazılınca form gönderilmiyordu. Kök neden yerel `url` giriş tipiydi: tarayıcının
 * kısıtı ŞEMASIZ adresi reddediyor ve gönderimi SESSİZCE durduruyordu — üstelik
 * sunucu tarafındaki Joi şeması (`httpUrlSchema`) protokolsüz adresi zaten
 * KABUL EDİP `https://` ekleyerek normalize ediyor. Yani tarayıcı, ürünün
 * kuralından daha katıydı.
 *
 * Alanlar artık metin girişi + `inputMode` URL klavyesiyle çizilir: klavye yine URL
 * klavyesidir, kısıt tek yerden (Joi) uygulanır.
 *
 * YARDIM METNİ KISALDI (kullanıcı kararı 30.08): eski hâli "https:// yazmasan da
 * olur, adresi olduğu gibi yapıştırabilirsin" diyordu — hiçbir gerçek ürün
 * kullanıcısına bunu açıklamaz, ders verir gibi okunuyordu. Alan ve davranış
 * AYNI kaldı; yalnız cümle "Örn. ornek.com" oldu. Şema `https://`yi kendisi
 * ekler, hata da alan altında görünür.
 *
 * `url-input.guard.spec.ts` deposu tarar: yerel `url` giriş tipi geri gelirse
 * test KIRILIR.
 */
export const urlInputProps = {
  type: 'text',
  inputMode: 'url',
  autoCapitalize: 'none',
  autoCorrect: 'off',
  spellCheck: false,
} as const;

type NativeTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  readonly invalid?: boolean;
};

export function Textarea({ className, invalid, ...rest }: NativeTextareaProps) {
  const describedBy = useDescribedBy(rest['aria-describedby']);
  return (
    <textarea
      {...rest}
      aria-describedby={describedBy}
      aria-invalid={ariaInvalid(invalid, rest['aria-invalid'])}
      className={[styles.input, invalid ? styles.inputError : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
    />
  );
}

type NativeSelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  readonly invalid?: boolean;
};

export function Select({ className, invalid, ...rest }: NativeSelectProps) {
  const describedBy = useDescribedBy(rest['aria-describedby']);
  return (
    <select
      {...rest}
      aria-describedby={describedBy}
      aria-invalid={ariaInvalid(invalid, rest['aria-invalid'])}
      className={[styles.input, invalid ? styles.inputError : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
    />
  );
}

/**
 * URL ALANI — tek bileşen (elle test 2 · bulgu 5).
 *
 * Etiket + metin girişi + "https:// yazmasan da olur" yardım metnini bir arada
 * verir. Alanların tek tek `urlInputProps` + `hint` taşıması hem tekrardı hem
 * de yardım metnini eklemeyi UNUTMAYA açıktı; kural artık tek yerde.
 */
export function UrlField({
  id,
  label,
  value,
  onChange,
  error,
}: {
  readonly id: string;
  readonly label: ReactNode;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Sunucudan gelen alan bazlı hata (`useFormErrors`) — alanın altına yazılır. */
  readonly error?: ReactNode;
}) {
  const t = useTranslation();
  return (
    <Field id={id} label={label} hint={t.t('common.urlHint')} error={error}>
      <Input
        id={id}
        {...urlInputProps}
        invalid={Boolean(error)}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}
